import React, { useState, useEffect } from "react";
import { CalendarClock, CheckCircle2, UserPlus, Sparkles, AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

/**
 * Three smart-pipeline transition modals for the Leads Kanban.
 *
 * Usage:
 *   const [modal, setModal] = useState(null); // {kind, lead}
 *   ...
 *   <LeadStageModals modal={modal} onClose={() => setModal(null)} onChanged={reload} />
 *
 * Modal kinds:
 *   - "schedule-trial"  for new/contacted → trial_booked
 *   - "complete-trial"  for trial_booked → trial_done
 *   - "convert"         for trial_done → converted
 */
export default function LeadStageModals({ modal, onClose, onChanged }) {
  if (!modal) return null;
  const { kind, lead } = modal;

  if (kind === "schedule-trial") {
    return <ScheduleTrialModal lead={lead} onClose={onClose} onChanged={onChanged} />;
  }
  if (kind === "complete-trial") {
    return <CompleteTrialModal lead={lead} onClose={onClose} onChanged={onChanged} />;
  }
  if (kind === "convert") {
    return <ConvertModal lead={lead} onClose={onClose} onChanged={onChanged} />;
  }
  return null;
}

// ----- Schedule Trial -----
function ScheduleTrialModal({ lead, onClose, onChanged }) {
  const today = new Date();
  const tmrw = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  const [date, setDate] = useState(tmrw.toISOString().slice(0, 10));
  const [time, setTime] = useState("18:00");
  const [trainerId, setTrainerId] = useState("");
  const [trainers, setTrainers] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    endpoints.listTrainers().then(({ data }) => setTrainers(data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Pick a trial date"); return; }
    setBusy(true);
    try {
      const trial_date = new Date(`${date}T${time || "18:00"}:00`).toISOString();
      await endpoints.scheduleTrial(lead.id, {
        trial_date,
        trial_time: time || null,
        trainer_id: trainerId || null,
        notes: notes || null,
      });
      toast.success("Trial scheduled", { description: "Task created for trial day." });
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule trial");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="schedule-trial-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-amber-600" /> Schedule trial - {lead.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="trial-date-input" required />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="trial-time-input" />
            </div>
          </div>
          {trainers.length > 0 && (
            <div>
              <Label className="text-xs">Trainer (optional)</Label>
              <Select value={trainerId || "_none"} onValueChange={(v) => setTrainerId(v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="trial-trainer-select"><SelectValue placeholder="Any trainer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Any trainer</SelectItem>
                  {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              data-testid="trial-notes-input"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Any prep - discipline, equipment, etc."
              className="min-h-[64px]"
            />
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted">
              Cancel
            </button>
            <button type="submit" disabled={busy} data-testid="schedule-trial-submit"
              className="px-4 py-2 text-sm rounded-md bg-amber-600 text-white font-medium hover:bg-amber-700 inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
              Schedule trial
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ----- Complete Trial -----
function CompleteTrialModal({ lead, onClose, onChanged }) {
  const [attended, setAttended] = useState(true);
  const [chance, setChance] = useState("medium");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await endpoints.completeTrial(lead.id, {
        attended,
        conversion_chance: attended ? chance : null,
        notes: notes || null,
      });
      toast.success(attended ? "Trial marked attended" : "Marked no-show - follow-up task created");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update trial");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="complete-trial-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Complete trial - {lead.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs mb-2 block">Trial attended?</Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setAttended(true)} data-testid="trial-attended-yes"
                className={`px-3 py-2.5 text-sm rounded-md border font-medium transition-colors ${
                  attended ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30" : "border-border bg-card hover:bg-muted"
                }`}>
                Yes - attended
              </button>
              <button type="button" onClick={() => setAttended(false)} data-testid="trial-attended-no"
                className={`px-3 py-2.5 text-sm rounded-md border font-medium transition-colors ${
                  !attended ? "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/30" : "border-border bg-card hover:bg-muted"
                }`}>
                No - no-show
              </button>
            </div>
          </div>

          {attended && (
            <div>
              <Label className="text-xs mb-2 block">Conversion chance</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "low",    label: "Low",    cls: "border-slate-400 bg-slate-50 text-slate-700 dark:bg-slate-950/30" },
                  { v: "medium", label: "Medium", cls: "border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950/30" },
                  { v: "high",   label: "High",   cls: "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30" },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => setChance(o.v)} data-testid={`chance-${o.v}`}
                    className={`px-3 py-2 text-xs rounded-md border font-semibold transition-colors ${
                      chance === o.v ? o.cls : "border-border bg-card hover:bg-muted text-muted-foreground"
                    }`}>{o.label}</button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea data-testid="trial-complete-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={attended ? "What went well? Concerns?" : "Why no-show? When to retry?"}
              className="min-h-[64px]" />
          </div>

          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted">Cancel</button>
            <button type="submit" disabled={busy} data-testid="complete-trial-submit"
              className="px-4 py-2 text-sm rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ----- Convert -----
const PLAN_PRESETS = [
  { plan: "Monthly", duration_days: 30, amount: 2500 },
  { plan: "Quarterly", duration_days: 90, amount: 7000 },
  { plan: "Half-Yearly", duration_days: 180, amount: 12000 },
  { plan: "Yearly", duration_days: 365, amount: 25000 },
];

function ConvertModal({ lead, onClose, onChanged }) {
  const [plan, setPlan] = useState(PLAN_PRESETS[0].plan);
  const [duration, setDuration] = useState(PLAN_PRESETS[0].duration_days);
  const [amount, setAmount] = useState(PLAN_PRESETS[0].amount);
  const [paid, setPaid] = useState("paid"); // paid | partial | pending
  const [paidAmount, setPaidAmount] = useState(PLAN_PRESETS[0].amount);
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  const onPlanChange = (v) => {
    setPlan(v);
    const preset = PLAN_PRESETS.find(p => p.plan === v);
    if (preset) {
      setDuration(preset.duration_days);
      setAmount(preset.amount);
      setPaidAmount(paid === "paid" ? preset.amount : 0);
    }
  };

  const onPaidChange = (v) => {
    setPaid(v);
    if (v === "paid") setPaidAmount(amount);
    if (v === "pending") setPaidAmount(0);
    if (v === "partial") setPaidAmount(Math.round(amount / 2));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || amount <= 0) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      await endpoints.convertLead(lead.id, {
        name: lead.name,
        phone: lead.phone,
        plan,
        plan_amount: parseFloat(amount),
        duration_days: parseInt(duration, 10) || 30,
        payment_status: paid,
        total_fee: parseFloat(amount),
        paid_amount: parseFloat(paidAmount || 0),
        due_date: paid !== "paid" && dueDate ? new Date(dueDate).toISOString() : null,
      });
      toast.success("Converted to member", { description: paid === "paid" ? "Payment recorded." : "Payment plan set." });
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not convert lead");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="convert-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-teal-600" /> Convert {lead.name} to member
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs">Membership plan</Label>
            <Select value={plan} onValueChange={onPlanChange}>
              <SelectTrigger data-testid="plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_PRESETS.map(p => (
                  <SelectItem key={p.plan} value={p.plan}>{p.plan} · {p.duration_days}d · ₹{p.amount}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-input" />
            </div>
            <div>
              <Label className="text-xs">Duration (days)</Label>
              <Input type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="duration-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs mb-2 block">Payment status</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "paid",    label: "Paid",    cls: "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30" },
                { v: "partial", label: "Partial", cls: "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/30" },
                { v: "pending", label: "Pending", cls: "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/30" },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => onPaidChange(o.v)} data-testid={`paid-${o.v}`}
                  className={`px-3 py-2 text-xs rounded-md border font-semibold ${
                    paid === o.v ? o.cls : "border-border bg-card hover:bg-muted text-muted-foreground"
                  }`}>{o.label}</button>
              ))}
            </div>
          </div>

          {paid !== "paid" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Paid now (₹)</Label>
                <Input type="number" min="0" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} data-testid="paid-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Due date</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="due-date-input" />
              </div>
            </div>
          )}

          {paid !== "paid" && (
            <div className="flex gap-2 items-start text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-md p-2.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>A payment-pending reminder task will be auto-created for this member.</span>
            </div>
          )}

          <div className="flex gap-2 items-start text-xs text-primary/85 bg-primary/5 border border-primary/20 rounded-md p-2.5">
            <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>This lead will land in <strong>"Ready to Onboard"</strong>. Capture personal &amp; emergency details there to activate the member and unlock automations.</span>
          </div>

          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted">Cancel</button>
            <button type="submit" disabled={busy} data-testid="convert-submit"
              className="px-4 py-2 text-sm rounded-md bg-teal-600 text-white font-medium hover:bg-teal-700 inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Convert to member
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Helper to derive which modal to open for a given drag transition.
 * Returns null if the transition is "free" (just direct status update).
 */
export function modalForTransition(fromStatus, toStatus) {
  // New/Contacted → Trial Booked
  if (toStatus === "trial_booked" && (fromStatus === "new" || fromStatus === "contacted")) {
    return "schedule-trial";
  }
  // Trial Booked → Trial Done
  if (toStatus === "trial_done" && fromStatus === "trial_booked") {
    return "complete-trial";
  }
  // Trial Done → Converted
  if (toStatus === "converted" && fromStatus === "trial_done") {
    return "convert";
  }
  // Any → Converted (skip-ahead allowed)
  if (toStatus === "converted") {
    return "convert";
  }
  return null;
}
